import React from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';
import {AwardIcon} from './AwardIcon';
import StarAsset from '@/Assets/Recognition/Star.svg';
import FlashAsset from '@/Assets/Recognition/Flash.svg';
import StreakAsset from '@/Assets/Recognition/Streak.svg';
import CrownAsset from '@/Assets/Recognition/Crown.svg';

const C = {
  card: '#FFFFFF',
  cardWarm: '#FFFBF0',
  brown: '#4D3F37',
  brownLight: '#4D3F37CC',
  gold: '#FACF47',
  goldDeep: '#E8A800',
  goldLight: '#FEF2B5',
  aqua: '#3DAB9E',
  coral: '#E17B21',
  purple: '#937ADB',
  border: 'rgba(38,35,34,0.12)',
  shadow: 'rgba(77,63,55,0.15)',
};

const AWARDS = [
  { key: 'good_try',      label: 'Good Try',      color: C.aqua,   tint: '#21CDBD20' },
  { key: 'great_effort',  label: 'Great Effort',  color: C.coral,  tint: '#FF854B20' },
  { key: 'good_thinking', label: 'Good Thinking', color: C.gold,   tint: C.goldLight },
  { key: 'focused',       label: 'Focused',       color: C.purple, tint: '#937ADB20' },
  { key: 'team_player',   label: 'Team Player',   color: C.brown,  tint: '#4D3F3712' },
];

const STREAK_TIERS = [
  { threshold: 7, name: 'LEGENDARY',   color: C.purple, asset: CrownAsset  },
  { threshold: 5, name: 'Unstoppable', color: C.coral,  asset: FlashAsset  },
  { threshold: 3, name: 'On a Roll',   color: C.gold,   asset: StreakAsset },
];

function tierFor(streak: number) {
  return STREAK_TIERS.find(t => streak >= t.threshold) ?? null;
}

interface ThinStudentStripProps {
  stars: number;
  streak: number;
  awards: string[];
  awardCounts?: Record<string, number>;
  compact?: boolean;
}

export function ThinStudentStrip({ stars, streak, awards, awardCounts, compact = false }: ThinStudentStripProps) {
  const tier = tierFor(streak);

  return (
    <View style={[styles.stripContainer, compact && styles.stripCompact]}>
      <View nativeID="cc-star-target" style={[styles.starsPill, stars > 0 && styles.starsPillActive]}>
        <Image source={StarAsset} style={styles.starImg} resizeMode="contain" />
        <Text style={[styles.starCount, stars > 0 && styles.starCountActive]}>{stars}</Text>
        <Text style={styles.starLabel}>stars</Text>
      </View>

      {tier && (
        <View style={[styles.streakPill, { backgroundColor: tier.color + '18', borderColor: tier.color }]}>
          <Image source={tier.asset} style={styles.streakImg} resizeMode="contain" />
          <Text style={[styles.streakTierName, { color: tier.color }]}>{tier.name}</Text>
          <View style={[styles.streakCountBadge, { backgroundColor: tier.color }]}>
            <Text style={styles.streakCountText}>{streak}</Text>
          </View>
        </View>
      )}

      {!compact && awards.length > 0 && (
        <>
          <View style={styles.divider} />
          <Text style={styles.earnedLabel}>Earned</Text>
          <View style={styles.awardsRow}>
            {awards.map((key, i) => {
              const award = AWARDS.find(a => a.key === key);
              if (!award) return null;
              const count = awardCounts?.[key] ?? 1;
              return (
                <View key={i} style={[styles.awardChip, { backgroundColor: award.tint, borderColor: award.color }]}>
                  <View style={styles.awardChipIconWrap}>
                    <AwardIcon awardKey={award.key} size={16} color={award.color} />
                  </View>
                  <Text style={[styles.awardChipLabel, { color: award.color }]}>{award.label}</Text>
                  <View style={[styles.awardCountBadge, { backgroundColor: award.color }]}>
                    <Text style={styles.awardCountText}>{count}</Text>
                  </View>
                </View>
              );
            })}
          </View>
        </>
      )}
    </View>
  );
}

interface ThinCoachStripProps {
  recognisedCount: number;
  totalCount: number;
  manualCount: number;
  mysteryCount: number;
  compact?: boolean;
}

export function ThinCoachStrip({
  recognisedCount,
  totalCount,
  manualCount,
  mysteryCount,
  compact = false,
}: ThinCoachStripProps) {
  const coveragePct = totalCount > 0 ? recognisedCount / totalCount : 0;
  const coverageColor =
    coveragePct >= 1 ? C.aqua
    : coveragePct >= 0.6 ? C.goldDeep
    : C.coral;

  return (
    <View style={[styles.stripContainer, compact && styles.stripCompact]}>
      <View style={[styles.starsPill, { backgroundColor: coverageColor + '15', borderColor: coverageColor + '50' }]}>
        <Image source={StarAsset} style={styles.starImg} resizeMode="contain" />
        <Text style={[styles.coverageCount, { color: coverageColor }]}>
          {recognisedCount}/{totalCount}
        </Text>
        <Text style={styles.starLabel}>recognised</Text>
      </View>

      <View style={styles.divider} />
      <View style={styles.metricGroup}>
        <Text style={styles.metricLabel}>Awards</Text>
        <View style={[styles.metricBadge, manualCount >= 12 ? { backgroundColor: C.coral + '20', borderColor: C.coral } : { backgroundColor: C.gold + '20', borderColor: C.goldDeep }]}>
          <Text style={[styles.metricBadgeText, { color: manualCount >= 12 ? C.coral : C.goldDeep }]}>{manualCount}<Text style={styles.metricBadgeMax}>/15</Text></Text>
        </View>
      </View>

      <View style={styles.divider} />
      <View style={styles.metricGroup}>
        <Image source={CrownAsset} style={styles.mysteryImg} resizeMode="contain" />
        <View style={[styles.metricBadge, { backgroundColor: C.purple + '20', borderColor: C.purple }]}>
          <Text style={[styles.metricBadgeText, { color: C.purple }]}>{mysteryCount}<Text style={styles.metricBadgeMax}>/2</Text></Text>
        </View>
        <Text style={styles.metricLabel}>Mystery</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  stripContainer: {
    minHeight: 44,
    flexShrink: 0,
    backgroundColor: C.cardWarm,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 5,
  },
  stripCompact: { minHeight: 36, gap: 8, paddingVertical: 3 },
  starsPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: C.border,
    backgroundColor: '#fff',
  },
  starsPillActive: {
    backgroundColor: C.goldLight,
    borderColor: C.goldDeep,
  },
  starIcon: { fontSize: 14 },
  starImg: { width: 18, height: 18 },
  streakImg: { width: 18, height: 18 },
  awardChipIconWrap: { width: 16, height: 16, alignItems: 'center', justifyContent: 'center' },
  mysteryImg: { width: 20, height: 20 },
  starCount: { fontSize: 15, fontWeight: '900', color: C.brownLight },
  starCountActive: { color: C.goldDeep },
  starLabel: { fontSize: 11, fontWeight: '600', color: C.brownLight },
  streakPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    borderWidth: 1.5,
  },
  streakIcon: { fontSize: 14 },
  streakTierName: { fontSize: 12, fontWeight: '900', letterSpacing: 0.3 },
  streakCountBadge: {
    minWidth: 22,
    height: 22,
    paddingHorizontal: 6,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  streakCountText: { fontSize: 12, fontWeight: '900', color: '#fff' },
  divider: { width: 1, height: 22, backgroundColor: C.border, marginHorizontal: 2 },
  earnedLabel: { fontSize: 11, fontWeight: '700', color: C.brownLight, textTransform: 'uppercase', letterSpacing: 0.5 },
  awardsRow: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  awardChip: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingVertical: 3, paddingHorizontal: 8,
    borderRadius: 999, borderWidth: 1.5,
  },
  awardChipIcon: { fontSize: 12 },
  awardChipLabel: { fontSize: 11, fontWeight: '800' },
  awardCountBadge: {
    minWidth: 16,
    height: 16,
    paddingHorizontal: 4,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 1,
  },
  awardCountText: { fontSize: 10, fontWeight: '900', color: '#fff' },
  coverageCount: { fontSize: 15, fontWeight: '900' },
  metricGroup: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  metricLabel: { fontSize: 11, fontWeight: '700', color: C.brownLight, textTransform: 'uppercase', letterSpacing: 0.4 },
  metricBadge: {
    flexDirection: 'row', alignItems: 'baseline',
    paddingHorizontal: 8, paddingVertical: 2,
    borderRadius: 999, borderWidth: 1.5,
  },
  metricBadgeText: { fontSize: 13, fontWeight: '900' },
  metricBadgeMax: { fontSize: 10, fontWeight: '700', opacity: 0.7 },
  mysteryIcon: { fontSize: 16 },
});
